// @ts-check
// Lógica da tela de importação sem componentes: leitura do arquivo, destinos e rótulos, linhas montadas,
// envio em lotes e planilha de erros. Os componentes do assistente só exibem e editam este estado.
import { signal } from '@preact/signals';
import {
  FAMILY_GROUPS, IMPORT_BATCH_SIZE, MAXIMUM_IMPORT_ROWS, SpreadsheetReadError, buildFamilyTemplateSampleRows, buildFamilyTemplateTargets,
  buildImportRow, buildImportTargets, familyKeyOf, findDuplicatesInFile, looksLikeZip, normalizeForMatching, parseCsv, readXlsx,
  resolveDuplicateCriteria,
} from '../../../../shared/spreadsheet-import/index.js';
import { importFamiliesBatch, importRecordsBatch } from '../../data/imports.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { ApiError, getErrorMessage } from '../../data/api-client.js';
import { buildCsv } from '../../lib/csv.js';
import { hasTranslation, t, translateMetadataLabel } from '../../i18n/index.js';

/**
 * @typedef {import('../../../../shared/spreadsheet-import/import-targets.js').ImportTarget} ImportTarget
 * @typedef {import('../../../../shared/spreadsheet-import/build-rows.js').BuiltImportRow} BuiltImportRow
 * @typedef {import('../../lib/field-metadata.js').ObjectMetadata} ObjectMetadata
 * @typedef {import('../../data/imports.js').ImportRowResult} ImportRowResult
 * @typedef {'object' | 'family'} ImportMode
 * @typedef {{ name: string, rows: string[][] }} Sheet
 * @typedef {{ status: 'CANCELLED' } & Omit<ImportRowResult, 'status'>} CancelledRowResult
 */

export { MAXIMUM_IMPORT_ROWS };
// objetos em que a tela oferece o modelo da escola, além da importação simples
export const FAMILY_TEMPLATE_OBJECTS = new Set(['person', 'household', 'enrollmentOpportunity', 'opportunityStudent']);
const PREVIEW_HEADER_ROWS = 10;
const SAMPLE_PRIMARY_SUB_FIELDS = new Set(['fullName', 'primaryEmail', 'primaryPhoneNumber', 'primaryLinkUrl', 'amountMicros', 'addressStreet1', 'markdown']);
const SAMPLE_EXTRA_SUB_FIELDS = new Set(['addressCity', 'addressState', 'addressPostcode']);

/** Incrementado ao fim de cada importação, para a lista do objeto recarregar. */
export const recordImportCounterState = signal(0);

/** Erro de leitura com a chave i18n da mensagem. */
export class SpreadsheetFileError extends Error {
  /** @param {string} messageKey @param {Record<string, unknown>} [variables] */
  constructor(messageKey, variables = {}) {
    super(t(messageKey, variables));
    this.messageKey = messageKey;
  }
}

/**
 * Texto de um CSV: UTF-8 quando válido; senão Windows-1252, o padrão do "CSV" do Excel em português.
 * @param {ArrayBuffer} buffer
 */
export function decodeCsvBytes(buffer) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1252').decode(buffer);
  }
}

/**
 * Lê CSV, TSV ou XLSX em abas de linhas de texto.
 * @param {File} file
 * @returns {Promise<Sheet[]>}
 */
export async function readSpreadsheetFile(file) {
  const buffer = await file.arrayBuffer();
  const lowerName = file.name.toLowerCase();
  let sheets;
  if (looksLikeZip(buffer)) {
    try {
      sheets = await readXlsx(buffer);
    } catch (error) {
      if (error instanceof SpreadsheetReadError && error.code === 'EMPTY_SPREADSHEET') throw new SpreadsheetFileError('spreadsheetImport.upload.empty');
      throw new SpreadsheetFileError('spreadsheetImport.upload.unreadable');
    }
  } else {
    const bytes = new Uint8Array(buffer.slice(0, 8));
    // assinatura do formato binário antigo do Excel (.xls, OLE2)
    if (lowerName.endsWith('.xls') || (bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0)) {
      throw new SpreadsheetFileError('spreadsheetImport.upload.legacyXls');
    }
    sheets = [{ name: file.name, rows: parseCsv(decodeCsvBytes(buffer)) }];
  }
  sheets = sheets.filter((sheet) => sheet.rows.length > 0);
  if (sheets.length === 0) throw new SpreadsheetFileError('spreadsheetImport.upload.empty');
  return sheets;
}

/**
 * Destinos do modo escolhido.
 * @param {ImportMode} mode @param {ObjectMetadata} objectMetadata
 * @returns {ImportTarget[]}
 */
export function buildTargetsForMode(mode, objectMetadata) {
  if (mode === 'family') return buildFamilyTemplateTargets((name) => /** @type {any} */ (objectsByNameSingular.value.get(name)));
  return buildImportTargets(/** @type {any} */ (objectMetadata));
}

/** @param {ImportTarget} target */
function fieldLabel(target) {
  const label = translateMetadataLabel(target.field.label);
  if (target.fieldName === 'id') return t('spreadsheetImport.match.id');
  if (!target.subFieldName) return label;
  if (target.subFieldName === 'fullName') return `${label} (${t('spreadsheetImport.match.fullName')})`;
  const subKey = `spreadsheetImport.subFields.${target.subFieldName}`;
  return `${label} / ${hasTranslation(subKey) ? t(subKey) : target.subFieldName}`;
}

/**
 * Rótulo do destino na tela; no modelo de famílias, com o grupo ("Responsável 1: Telefones / Telefone principal").
 * @param {ImportTarget} target
 */
export function targetLabel(target) {
  if (target.group === 'record') return fieldLabel(target);
  return `${t(`spreadsheetImport.groups.${target.group}`)}: ${fieldLabel(target)}`;
}

/**
 * Grupos de opções do seletor de campo.
 * @param {ImportMode} mode @param {ImportTarget[]} targets
 * @returns {Array<{ label: string, targets: ImportTarget[] }>}
 */
export function groupTargetsForSelect(mode, targets) {
  if (mode === 'family') {
    return FAMILY_GROUPS.map((group) => ({ label: t(`spreadsheetImport.groups.${group}`), targets: targets.filter((target) => target.group === group) }))
      .filter((group) => group.targets.length > 0);
  }
  const byLabel = (/** @type {ImportTarget} */ left, /** @type {ImportTarget} */ right) => targetLabel(left).localeCompare(targetLabel(right), 'pt-BR');
  return [
    { label: t('spreadsheetImport.match.groupFields'), targets: targets.filter((target) => target.kind !== 'RELATION').sort(byLabel) },
    { label: t('spreadsheetImport.match.groupRelations'), targets: targets.filter((target) => target.kind === 'RELATION').sort(byLabel) },
  ].filter((group) => group.targets.length > 0);
}

/**
 * Linhas da planilha de exemplo: no modo objeto, um cabeçalho por campo (o sub-campo principal dos compostos).
 * @param {ImportMode} mode @param {ImportTarget[]} targets
 */
export function buildSampleRows(mode, targets) {
  if (mode === 'family') return buildFamilyTemplateSampleRows();
  const headers = [];
  for (const target of targets) {
    if (target.fieldName === 'id') continue;
    if (!target.subFieldName) headers.push(translateMetadataLabel(target.field.label));
    // o rótulo puro do campo casa com o sub-campo principal na volta (nome completo, e-mail, telefone...)
    else if (SAMPLE_PRIMARY_SUB_FIELDS.has(target.subFieldName)) headers.push(translateMetadataLabel(target.field.label));
    else if (SAMPLE_EXTRA_SUB_FIELDS.has(target.subFieldName)) headers.push(fieldLabel(target));
  }
  return [headers];
}

/** Opções de linha de cabeçalho: as primeiras linhas da aba com uma prévia do conteúdo. @param {string[][]} rows */
export function headerRowChoices(rows) {
  return rows.slice(0, PREVIEW_HEADER_ROWS).map((row, index) => ({
    index,
    preview: row.filter((cell) => cell.trim() !== '').slice(0, 4).join(', ').slice(0, 60),
  }));
}

/**
 * Valores distintos de uma coluna que não casam com nenhuma opção do campo de seleção (até 30).
 * @param {string[][]} dataRows @param {number} columnIndex @param {ImportTarget} target
 */
export function findUnmatchedSelectValues(dataRows, columnIndex, target) {
  const options = target.field.options ?? [];
  const known = new Set(options.flatMap((option) => [normalizeForMatching(option.value), normalizeForMatching(option.label)]));
  /** @type {Map<string, string>} */
  const unmatched = new Map();
  for (const row of dataRows) {
    const cell = String(row[columnIndex] ?? '').trim();
    if (cell === '') continue;
    const parts = target.kind === 'MULTI_SELECT' ? cell.split(/\s*[;,|\n]\s*/) : [cell];
    for (const part of parts) {
      const normalized = normalizeForMatching(part);
      if (!normalized || known.has(normalized) || unmatched.has(normalized)) continue;
      // rótulo abreviado que ainda casa por prefixo não precisa de escolha manual
      if (options.filter((option) => normalizeForMatching(option.label).startsWith(normalized)).length === 1 && normalized.length >= 3) continue;
      unmatched.set(normalized, part);
      if (unmatched.size >= 30) return [...unmatched.entries()];
    }
  }
  return [...unmatched.entries()];
}

/**
 * Monta todas as linhas de dados com o mapeamento atual; marca as repetidas na planilha (modo objeto).
 * @param {{ mode: ImportMode, objectMetadata: ObjectMetadata, dataRows: string[][], firstRowNumber: number, mapping: Array<string | null>,
 *   valueMappings: Record<number, Record<string, string>>, targets: ImportTarget[], duplicateCriteriaKeys: string[] }} input
 * @returns {BuiltImportRow[]}
 */
export function buildRows({ mode, objectMetadata, dataRows, firstRowNumber, mapping, valueMappings, targets, duplicateCriteriaKeys }) {
  const targetsByKey = new Map(targets.map((target) => [target.key, target]));
  const columns = mapping
    .map((targetKey, columnIndex) => (targetKey ? { columnIndex, targetKey, valueMapping: valueMappings[columnIndex] } : null))
    .filter((column) => column !== null);
  // só os obrigatórios ligados a uma coluna geram aviso por linha; os sem coluna aparecem uma vez no mapeamento
  const mappedKeys = new Set(columns.map((column) => /** @type {{ targetKey: string }} */ (column).targetKey));
  const requiredTargets = new Map([...targetsByKey].map(([key, target]) => [key, mappedKeys.has(key) ? target : { ...target, isRequired: false }]));
  const built = dataRows.map((row, index) => buildImportRow(row, firstRowNumber + index, /** @type {any} */ (columns), requiredTargets));
  if (mode === 'object') {
    const criteria = resolveDuplicateCriteria(/** @type {any} */ (objectMetadata)).filter((criterion) => duplicateCriteriaKeys.includes(criterion.key));
    const duplicates = findDuplicatesInFile(built.map((row) => ({ rowNumber: row.rowNumber, values: row.groups.record?.values ?? {} })), criteria);
    for (const [index, firstRow] of duplicates) {
      built[index].warnings.push({ columnIndex: null, targetKey: null, code: 'duplicateOfRow', params: { row: firstRow } });
    }
  }
  return built;
}

/**
 * Mensagem de um erro ou aviso de célula.
 * @param {{ code: string, params?: Record<string, string | number> }} issue
 */
export function issueMessage(issue) {
  if (issue.code === 'duplicateOfRow') return t('spreadsheetImport.validate.duplicateOfRow', issue.params ?? {});
  const key = `spreadsheetImport.cellErrors.${issue.code}`;
  return hasTranslation(key) ? t(key, issue.params ?? {}) : issue.code;
}

/**
 * Mensagem de uma linha recusada pelo servidor, com o rótulo do campo quando conhecido.
 * @param {{ errorCode?: string, message?: string, fieldName?: string | null }} result @param {ImportTarget[]} targets
 */
export function serverErrorMessage(result, targets) {
  const fieldName = result.fieldName ?? '';
  const target = targets.find((candidate) => candidate.fieldName === fieldName || `${candidate.group}.${candidate.fieldName}` === fieldName);
  const field = target ? targetLabel(target) : fieldName;
  const key = `spreadsheetImport.serverErrors.${result.errorCode}`;
  const detail = result.message ?? '';
  return hasTranslation(key) ? t(key, { field, detail }) : t('spreadsheetImport.serverErrors.UNKNOWN', { detail });
}

/**
 * Divide as linhas em lotes de até 200; no modelo de famílias, cada família fica inteira num lote.
 * @param {ImportMode} mode @param {Array<{ rowNumber: number, familyKey?: string }>} records
 */
export function splitIntoBatches(mode, records) {
  if (mode !== 'family') {
    const batches = [];
    for (let start = 0; start < records.length; start += IMPORT_BATCH_SIZE) batches.push(records.slice(start, start + IMPORT_BATCH_SIZE));
    return batches;
  }
  /** @type {Map<string, typeof records>} */
  const families = new Map();
  for (const record of records) {
    const list = families.get(/** @type {string} */ (record.familyKey)) ?? [];
    list.push(record);
    families.set(/** @type {string} */ (record.familyKey), list);
  }
  /** @type {Array<typeof records>} */
  const batches = [];
  let current = [];
  for (const family of families.values()) {
    if (current.length > 0 && current.length + family.length > IMPORT_BATCH_SIZE) {
      batches.push(current);
      current = [];
    }
    for (let start = 0; start < family.length; start += IMPORT_BATCH_SIZE) {
      const part = family.slice(start, start + IMPORT_BATCH_SIZE);
      if (part.length === IMPORT_BATCH_SIZE) batches.push(part);
      else current.push(...part);
    }
  }
  if (current.length > 0) batches.push(current);
  return batches;
}

/**
 * Envia as linhas válidas em lotes, em sequência, com progresso e parada entre lotes.
 * @param {{ mode: ImportMode, objectMetadata: ObjectMetadata, rows: BuiltImportRow[], duplicateStrategy: string, duplicateCriteriaKeys: string[],
 *   createMissingRelations: string[], onProgress: (done: number) => void, shouldStop: () => boolean }} input
 */
export async function sendImport({ mode, objectMetadata, rows, duplicateStrategy, duplicateCriteriaKeys, createMissingRelations, onProgress, shouldStop }) {
  const records = rows.map((row) => (mode === 'family'
    ? { rowNumber: row.rowNumber, familyKey: familyKeyOf(row.groups, row.rowNumber), groups: row.groups }
    : { rowNumber: row.rowNumber, values: row.groups.record?.values ?? {}, relations: row.groups.record?.relations ?? {} }));
  /** @type {Array<ImportRowResult | CancelledRowResult>} */
  const results = [];
  /** @type {import('../../data/imports.js').ImportBatchResult['createdRelations']} */
  const createdRelations = [];
  /** @type {Record<string, number>} */
  const counters = {};
  let done = 0;
  let cancelled = false;
  for (const batch of splitIntoBatches(mode, records)) {
    if (shouldStop()) {
      cancelled = true;
      for (const record of batch) results.push({ rowNumber: record.rowNumber, status: 'CANCELLED', recordId: null });
      continue;
    }
    try {
      const outcome = mode === 'family'
        ? await importFamiliesBatch({ records: batch, duplicateStrategy, createMissingRelations })
        : await importRecordsBatch(objectMetadata.namePlural, {
          records: batch, duplicateStrategy, duplicateCriteria: duplicateCriteriaKeys, createMissingRelations,
        });
      results.push(...outcome.results);
      createdRelations.push(...outcome.createdRelations);
      for (const [key, value] of Object.entries(outcome.counters ?? {})) counters[key] = (counters[key] ?? 0) + value;
    } catch (error) {
      const detail = error instanceof ApiError ? getErrorMessage(error) : String(error);
      for (const record of batch) results.push({ rowNumber: record.rowNumber, status: 'FAILED', recordId: null, errorCode: 'REQUEST_FAILED', message: detail });
    }
    done += batch.length;
    onProgress(done);
  }
  return { results, createdRelations, counters, cancelled };
}

/**
 * Planilha de erros: número da linha, as colunas originais e o motivo.
 * @param {{ headers: string[], dataRows: string[][], firstRowNumber: number, failures: Array<{ rowNumber: number, message: string }> }} input
 */
export function buildErrorCsv({ headers, dataRows, firstRowNumber, failures }) {
  const sorted = [...failures].sort((left, right) => left.rowNumber - right.rowNumber);
  return buildCsv([
    [t('spreadsheetImport.report.rowColumn'), ...headers, t('spreadsheetImport.report.errorColumn')],
    ...sorted.map((failure) => [failure.rowNumber, ...(dataRows[failure.rowNumber - firstRowNumber] ?? []), failure.message]),
  ]);
}
